import Box from '@mui/material/Box';
import InputBase from '@mui/material/InputBase';
import Typography from '@mui/material/Typography';
import makeStyles from '@mui/styles/makeStyles';
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  Elements,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { StripeCardNumberElementChangeEvent, StripeElementChangeEvent } from '@stripe/stripe-js';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { fromPaymentMethod, useAppDispatch } from '../../../../store';
import { getStripe } from '../../../../utils/stripeClient';
import { CARD_BRAND_LIST } from '../Constants';

/** Brand logos shown on the card-number row, in the order of the Payment & Billing design. */
const DISPLAYED_BRANDS = ['visa', 'mastercard', 'americanexpress', 'discover'];

const useStyles = makeStyles(() => ({
  root: {
    fontFamily: 'Inter, -apple-system, system-ui, sans-serif',
    '& .MuiTypography-root': {
      fontFamily: 'Inter, -apple-system, system-ui, sans-serif',
    },
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    minHeight: 32,
  },
  headerLabel: {
    fontSize: 14,
    fontWeight: 600,
    color: '#16141d',
  },
  required: {
    color: '#d14343',
  },
  group: {
    border: '1px solid #cdd6d2',
    borderRadius: 10,
    overflow: 'hidden',
    backgroundColor: '#fff',
    marginTop: 8,
    '&:focus-within': {
      borderColor: '#0f8461',
    },
  },
  groupError: {
    borderColor: '#d14343',
  },
  numberRow: {
    position: 'relative',
    borderBottom: '1px solid #cdd6d2',
    height: 36,
    display: 'flex',
    alignItems: 'center',
    padding: '0 150px 0 14px',
    boxSizing: 'border-box',
  },
  elementHost: {
    flex: 1,
    minWidth: 0,
  },
  brands: {
    position: 'absolute',
    right: 14,
    top: '50%',
    transform: 'translateY(-50%)',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
  },
  brandLogo: {
    height: 14,
    width: 'auto',
    display: 'block',
    transition: 'opacity .15s ease',
  },
  brandLogoDimmed: {
    opacity: 0.3,
  },
  detailsRow: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr) minmax(0, 1fr)',
  },
  cell: {
    height: 36,
    display: 'flex',
    alignItems: 'center',
    padding: '0 14px',
    boxSizing: 'border-box',
    minWidth: 0,
    borderRight: '1px solid #cdd6d2',
    '&:last-child': {
      borderRight: 'none',
    },
  },
  nameInput: {
    width: '100%',
    fontSize: 14,
    color: '#16141d',
    fontFamily: 'inherit',
    '& input': {
      padding: 0,
      height: 'auto',
      '&::placeholder': {
        color: '#7f8b86',
        opacity: 1,
      },
    },
  },
  note: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginTop: 8,
    fontSize: 12,
    lineHeight: 1.5,
    color: '#5b6661',
    minWidth: 0,
  },
  noteIcon: {
    display: 'inline-flex',
    flex: 'none',
    color: '#0f8461',
    marginTop: 1,
  },
  noteText: {
    minWidth: 0,
  },
  errorMessage: {
    marginTop: 6,
    fontSize: 12,
    color: '#d14343',
  },
  savedCard: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '12px 16px',
    border: '1px solid #cdd6d2',
    borderRadius: 12,
    backgroundColor: '#fff',
  },
  savedLogo: {
    flex: 'none',
    width: 44,
    height: 30,
    border: '1px solid #e1e7e4',
    borderRadius: 4,
    backgroundColor: '#fff',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  savedTitle: {
    display: 'block',
    fontSize: 14,
    fontWeight: 600,
    color: '#16141d',
  },
  savedBrand: {
    textTransform: 'capitalize',
  },
  savedMeta: {
    display: 'block',
    marginTop: 2,
    fontSize: 12,
    color: '#5b6661',
  },
  defaultBadge: {
    flex: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    height: 20,
    padding: '0 8px',
    borderRadius: 999,
    backgroundColor: '#e6f4ee',
    fontSize: 12,
    fontWeight: 600,
    color: '#0b6b4f',
  },
}));

const ELEMENT_STYLE = {
  style: {
    base: {
      fontSize: '14px',
      color: '#16141d',
      fontFamily: 'Inter, -apple-system, system-ui, sans-serif',
      fontSmoothing: 'antialiased',
      '::placeholder': {
        color: '#7f8b86',
      },
    },
    invalid: {
      color: '#d14343',
    },
  },
};

const LockIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="4" y="10" width="16" height="10" rx="2" />
    <path d="M8 10V7a4 4 0 0 1 8 0v3" />
  </svg>
);

/** Billing details attached to the PaymentMethod (helps Stripe Radar / AVS). */
export interface InlineCardBillingDetails {
  email?: string;
  address?: {
    country?: string;
    line1?: string;
    city?: string;
    state?: string;
    postal_code?: string;
  };
}

export interface InlineCardFormHandle {
  /**
   * Tokenises the card entered in the Stripe Elements into a `pm_` id (the card never touches
   * our servers). Rejects with the Stripe error message when the card is invalid.
   */
  createPaymentMethod: (billingDetails: InlineCardBillingDetails) => Promise<string>;
  /**
   * Tokenises the entered card into a `tok_` token for the save-card API (`POST
   * /payment-methods`), which attaches it to the account as the default card without charging.
   * Rejects with the Stripe error message when the card is invalid.
   */
  createToken: () => Promise<string>;
  /** True when a NEW card is being entered (no saved card, or the saved card is being changed). */
  isEnteringNewCard: () => boolean;
  /** Something was typed into the card elements or the cardholder name. */
  isDirty: () => boolean;
  /** Every element is complete and the cardholder name is filled. */
  isComplete: () => boolean;
  /** Empties the elements and the cardholder name. */
  clear: () => void;
}

interface InlineCardFormProps {
  accountId: number;
  /**
   * The saved card is being replaced ("Change" in the Payment method header). Ignored when the
   * account has no saved card — the elements are then always shown.
   */
  changingCard?: boolean;
}

const stripFalsy = <T extends Record<string, string | undefined>>(value: T) => (
  Object.fromEntries(Object.entries(value).filter(([, v]) => !!v)) as Record<string, string>
);

/**
 * Card entry block of the Payment & Billing page (flag `inlineCardCheckoutEnabled`, plan D2):
 * - no saved card → the grouped Stripe Elements (number · name | MM/YY | CVC) are shown;
 * - saved card → "Visa ending 4242" with its Default badge, until the section's "Change" opens
 *   the elements again (`changingCard`).
 * The page reads the entered card through the imperative handle at checkout time; the
 * `cardInformationInvalid` store flag mirrors whether the open form is complete so the page can
 * disable its Pay button.
 */
const InlineCardFormInner = forwardRef<InlineCardFormHandle, InlineCardFormProps>((
  { accountId, changingCard = false },
  ref,
) => {
  const classes = useStyles();
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const stripe = useStripe();
  const elements = useElements();
  const currentCard = useSelector(fromPaymentMethod.selectOneByAccountId(accountId));

  const [cardholderName, setCardholderName] = useState('');
  const [currentCardBrand, setCurrentCardBrand] = useState('unknown');
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const [isCardNumberValid, setIsCardNumberValid] = useState(false);
  const [isCardCvcValid, setIsCardCvcValid] = useState(false);
  const [isCardExpiryValid, setIsCardExpiryValid] = useState(false);
  // Which elements hold something (complete or not): the "unsaved changes" check of the section.
  const [filled, setFilled] = useState({ number: false, expiry: false, cvc: false });

  const enteringNewCard = !currentCard || changingCard;
  const isComplete = isCardNumberValid && isCardCvcValid && isCardExpiryValid
    && cardholderName.trim().length > 0 && !errorMessage;
  const isDirty = filled.number || filled.expiry || filled.cvc || cardholderName.trim().length > 0;

  // The page's Pay now waits for a complete card only when the card entered here is the one
  // the order will be charged on (no card on file). A saved card being edited goes through
  // the section's Save / "Save and pay" instead, so an unfinished edit must not lock Pay now.
  useEffect(() => {
    dispatch(fromPaymentMethod.doUpdateCardInformationInvalid(
      enteringNewCard && !currentCard && !isComplete,
    ));
  }, [enteringNewCard, currentCard, isComplete, dispatch]);

  const clearElements = () => {
    elements?.getElement(CardNumberElement)?.clear();
    elements?.getElement(CardExpiryElement)?.clear();
    elements?.getElement(CardCvcElement)?.clear();
    setCardholderName('');
    setIsCardNumberValid(false);
    setIsCardCvcValid(false);
    setIsCardExpiryValid(false);
    setFilled({ number: false, expiry: false, cvc: false });
    setCurrentCardBrand('unknown');
    setErrorMessage(undefined);
  };

  // Leaving the form (payment method switched, flag off) must not keep the page's button locked.
  useEffect(() => () => {
    dispatch(fromPaymentMethod.doUpdateCardInformationInvalid(false));
  }, [dispatch]);

  // Leaving the edit of a saved card discards whatever was typed into the elements.
  useEffect(() => {
    if (!changingCard && currentCard) {
      clearElements();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [changingCard, currentCard, elements]);

  useImperativeHandle(ref, () => ({
    isEnteringNewCard: () => enteringNewCard,
    isDirty: () => isDirty,
    isComplete: () => isComplete,
    clear: clearElements,
    createToken: async () => {
      const cardNumberElement = elements?.getElement(CardNumberElement);
      if (!stripe || !cardNumberElement) {
        throw new Error(intl.formatMessage({ id: 'checkout.card.not.ready' }));
      }
      const { token, error } = await stripe.createToken(cardNumberElement, {
        name: cardholderName.trim(),
      });
      if (error || !token) {
        const message = error?.message ?? intl.formatMessage({ id: 'checkout.card.invalid' });
        setErrorMessage(message);
        throw new Error(message);
      }
      return token.id;
    },
    createPaymentMethod: async (billingDetails: InlineCardBillingDetails) => {
      const cardNumberElement = elements?.getElement(CardNumberElement);
      if (!stripe || !cardNumberElement) {
        throw new Error(intl.formatMessage({ id: 'checkout.card.not.ready' }));
      }
      const address = stripFalsy(billingDetails.address ?? {});
      const name = cardholderName.trim();
      const { paymentMethod, error } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardNumberElement,
        billing_details: {
          ...(name ? { name } : {}),
          ...(billingDetails.email ? { email: billingDetails.email } : {}),
          ...(Object.keys(address).length > 0 ? { address } : {}),
        },
      });
      if (error || !paymentMethod) {
        const message = error?.message ?? intl.formatMessage({ id: 'checkout.card.invalid' });
        setErrorMessage(message);
        throw new Error(message);
      }
      return paymentMethod.id;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [stripe, elements, enteringNewCard, isDirty, isComplete, cardholderName, intl]);

  const handleChangeCardNumber = (e: StripeCardNumberElementChangeEvent) => {
    setCurrentCardBrand(e.brand ?? 'unknown');
    setIsCardNumberValid(e.error === undefined && !e.empty && e.complete);
    setFilled(current => ({ ...current, number: !e.empty }));
    setErrorMessage(e.error?.message);
  };
  const handleCvcChange = (e: StripeElementChangeEvent) => {
    setIsCardCvcValid(e.error === undefined && !e.empty && e.complete);
    setFilled(current => ({ ...current, cvc: !e.empty }));
    setErrorMessage(e.error?.message);
  };
  const handleExpiryChange = (e: StripeElementChangeEvent) => {
    setIsCardExpiryValid(e.error === undefined && !e.empty && e.complete);
    setFilled(current => ({ ...current, expiry: !e.empty }));
    setErrorMessage(e.error?.message);
  };

  const brandLogos = DISPLAYED_BRANDS
    .map(brand => CARD_BRAND_LIST.find(it => it.brand === brand))
    .filter((it): it is NonNullable<typeof it> => !!it);
  const knownBrand = currentCardBrand !== 'unknown';
  const savedCardLogo = currentCard
    ? CARD_BRAND_LIST.find(it => it.brand === currentCard.brand?.toLowerCase())
    : undefined;

  if (!enteringNewCard && currentCard) {
    return (
      <Box id="inline-card-saved" className={classes.root}>
        <Box className={classes.savedCard}>
          <Box component="span" className={classes.savedLogo}>
            {savedCardLogo && (
              <img src={savedCardLogo.src} alt={currentCard.brand} className={classes.brandLogo} />
            )}
          </Box>
          <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
            <Typography component="span" className={classes.savedTitle}>
              <FormattedMessage
                id="page.order.confirmation.v2.card.saved.ending"
                values={{
                  brand: <Box component="span" className={classes.savedBrand}>{currentCard.brand}</Box>,
                  last4: currentCard.last4,
                }}
              />
            </Typography>
            <Typography component="span" className={classes.savedMeta}>
              <FormattedMessage
                id="page.order.confirmation.v2.card.saved.expires"
                values={{ expiration: currentCard.expiration }}
              />
              {currentCard.name ? ` · ${currentCard.name}` : ''}
            </Typography>
          </Box>
          <Box component="span" className={classes.defaultBadge}>
            <FormattedMessage id="page.order.confirmation.v2.card.saved.default" />
          </Box>
        </Box>
        <Box className={classes.note}>
          <Box component="span" className={classes.noteIcon}><LockIcon /></Box>
          <Box component="span" className={classes.noteText}>
            <FormattedMessage id="page.order.confirmation.v2.card.saved.charged" />
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box id="inline-card-form" className={classes.root}>
      <Box className={classes.header}>
        <Typography component="span" className={classes.headerLabel}>
          <FormattedMessage id="page.order.confirmation.v2.card.information" />
          <Box component="span" aria-hidden="true" className={classes.required}> *</Box>
        </Typography>
      </Box>
      <Box
        id="inline-card-elements"
        className={`${classes.group} ${errorMessage ? classes.groupError : ''}`}
      >
        <Box className={classes.numberRow}>
          <Box className={classes.elementHost}>
            <CardNumberElement
              options={{
                ...ELEMENT_STYLE,
                // No Stripe Link "Autofill" / "Save with Link" overlay — not part of the design.
                disableLink: true,
                placeholder: intl.formatMessage({ id: 'page.order.confirmation.v2.card.number.placeholder' }),
              }}
              onChange={handleChangeCardNumber}
            />
          </Box>
          <Box component="span" className={classes.brands}>
            {brandLogos.map(brand => (
              <img
                key={brand.brand}
                src={brand.src}
                alt={brand.brand}
                className={`${classes.brandLogo} ${knownBrand && currentCardBrand.toLowerCase() !== brand.brand
                  ? classes.brandLogoDimmed
                  : ''}`}
              />
            ))}
          </Box>
        </Box>
        <Box className={classes.detailsRow}>
          <Box className={classes.cell}>
            <InputBase
              id="inline-card-holder"
              className={classes.nameInput}
              value={cardholderName}
              onChange={event => setCardholderName(event.target.value)}
              placeholder={intl.formatMessage({ id: 'page.order.confirmation.v2.card.holder.placeholder' })}
              inputProps={{
                'aria-label': intl.formatMessage({ id: 'page.order.confirmation.v2.card.holder.placeholder' }),
                autoComplete: 'cc-name',
              }}
            />
          </Box>
          <Box className={classes.cell}>
            <Box className={classes.elementHost}>
              <CardExpiryElement
                options={{
                  ...ELEMENT_STYLE,
                  placeholder: intl.formatMessage({ id: 'page.order.confirmation.v2.card.expiry.placeholder' }),
                }}
                onChange={handleExpiryChange}
              />
            </Box>
          </Box>
          <Box className={classes.cell}>
            <Box className={classes.elementHost}>
              <CardCvcElement
                options={{
                  ...ELEMENT_STYLE,
                  placeholder: intl.formatMessage({ id: 'page.order.confirmation.v2.card.cvc.placeholder' }),
                }}
                onChange={handleCvcChange}
              />
            </Box>
          </Box>
        </Box>
      </Box>
      {errorMessage && (
        <Typography id="inline-card-error" className={classes.errorMessage}>
          {errorMessage}
        </Typography>
      )}
      <Box className={classes.note}>
        <Box component="span" className={classes.noteIcon}><LockIcon /></Box>
        <Box component="span" className={classes.noteText}>
          <FormattedMessage id="page.order.confirmation.v2.card.saved.note" />
        </Box>
      </Box>
    </Box>
  );
});

export const InlineCardForm = forwardRef<InlineCardFormHandle, InlineCardFormProps>((
  props,
  ref,
) => {
  const dispatch = useAppDispatch();

  // The saved card (if any) decides whether the elements open straight away (D2).
  useEffect(() => {
    if (!props.accountId) return;
    dispatch(fromPaymentMethod.doGetPaymentMethod(props.accountId))
      .catch(() => {
        // no saved card: the form simply opens
      });
  }, [props.accountId, dispatch]);

  return (
    <Elements stripe={getStripe()}>
      <InlineCardFormInner ref={ref} {...props} />
    </Elements>
  );
});
